/**
 * EMZI CI (Corporate Identity) Design System - OFFICIAL STANDARD
 * ================================================================
 * This is the LOCKED and APPROVED design framework for the entire Emergent system.
 * All modules MUST use these constants for visual consistency.
 * 
 * Last Updated: October 2025
 * Status: ✅ APPROVED & LOCKED
 * 
 * DO NOT modify these values without design team approval.
 * For new modules, import and use these constants directly.
 */

export const EMZI_COLORS = {
  // ==========================================
  // PRIMARY THEME COLORS (EMZI NEXUS v6.4 - UPDATED)
  // ==========================================
  primary: '#0e67a2',       // EMZI NEXUS Blue - Headers, Primary Actions
  primaryHover: '#0a5a8a',  // EMZI NEXUS Blue Hover - Hover states
  accent: '#FF7B00',        // EMZI Orange (Flat) - CTA Buttons, Badges
  accentHover: '#E66D00',   // EMZI Orange Hover - Hover states
  background: '#FFFFFF',    // White - Main background
  surface: '#F9FAFB',       // Light Grey - Card backgrounds
  
  // ==========================================
  // TEXT COLORS (APPROVED & LOCKED)
  // ==========================================
  textPrimary: '#333333',   // Charcoal - Primary text
  textSecondary: '#666666', // Grey - Secondary text
  textMuted: '#999999',     // Light Grey - Muted text
  textWhite: '#FFFFFF',     // White - Text on blue backgrounds
  
  // ==========================================
  // STATUS COLORS (APPROVED & LOCKED)
  // ==========================================
  success: '#16A34A',       // Green - Success states
  error: '#DC2626',         // Red - Error states
  warning: '#F59E0B',       // Amber - Warning states
  info: '#0EA5E9',          // Sky Blue - Info states
  
  // ==========================================
  // UI ELEMENTS (FLAT CI v6.3 - LOCKED)
  // ==========================================
  border: '#E5E7EB',                      // Standard borders (flat gray)
  borderLight: '#F3F4F6',                 // Light borders
  borderWhite: '#FFFFFF',                 // White borders
  divider: '#E5E7EB',                     // Divider lines
  shadow: 'rgba(0, 0, 0, 0.08)',          // Shadow color
  overlay: 'rgba(0, 0, 0, 0.5)',          // Modal overlays
  hoverBg: '#F3F4F6',                     // Hover background (flat)
};

// ==========================================
// SHADOWS (APPROVED & LOCKED)
// ==========================================
export const EMZI_SHADOWS = {
  // Card Shadows
  sm: '0 2px 8px rgba(0, 0, 0, 0.06)',          // Small cards
  md: '0 4px 12px rgba(0, 0, 0, 0.1)',          // Medium cards
  lg: '0 6px 20px rgba(0, 0, 0, 0.15)',         // Large cards
  xl: '0 8px 32px rgba(0, 0, 0, 0.2)',          // Extra large (modals)
  
  // Component Specific
  card: '0 4px 20px rgba(0, 0, 0, 0.08)',       // Standard card
  cardHover: '0 8px 32px rgba(0, 0, 0, 0.12)',  // Card hover state
  button: '0 2px 8px rgba(0, 0, 0, 0.1)',       // Button shadow
  buttonHover: '0 4px 12px rgba(0, 0, 0, 0.15)', // Button hover
  header: '0 4px 16px rgba(0, 0, 0, 0.1)',      // Header shadow
  
  // Special Effects
  blueGlow: '0 4px 16px rgba(0, 122, 255, 0.2)', // Blue glow effect
};

// ==========================================
// SPACING (FLAT CI v6.3 - COMPACT STANDARD)
// ==========================================
export const EMZI_SPACING = {
  xs: '4px',      // Extra small - Tight gaps
  sm: '8px',      // Small - Component internal spacing
  md: '12px',     // Medium - Standard spacing (COMPACT DEFAULT)
  lg: '16px',     // Large - Section spacing
  xl: '20px',     // Extra large - Page margins
  xxl: '24px',    // Double extra large - Major sections
  xxxl: '32px',   // Triple extra large - Page padding
  
  // Compact Variants (v6.3)
  compact: '12px',      // Compact card padding (REDUCED)
  compactSection: '12px', // Compact section gaps
  compactCard: '8px',   // Compact card internal spacing
};

// ==========================================
// BORDER RADIUS (APPROVED & LOCKED)
// ==========================================
export const EMZI_RADIUS = {
  sm: '6px',      // Small - Badges, tags (COMPACT)
  md: '8px',      // Medium - Buttons, cards (COMPACT STANDARD)
  lg: '12px',     // Large - Panels, containers
  xl: '16px',     // Extra large - Modal dialogs
  full: '9999px', // Full - Pills, circular elements
};

export const EMZI_TYPOGRAPHY = {
  // Mobile-First Typography (Flat CI v6.3)
  title: {
    fontSize: '22px',
    fontWeight: '700',
    lineHeight: '1.2',
  },
  h2: {
    fontSize: '18px',
    fontWeight: '600',
    lineHeight: '1.3',
  },
  body: {
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '1.5',
  },
  caption: {
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.4',
  },
  label: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '1.3',
  },
  // Legacy (for backward compatibility)
  pageTitle: {
    fontSize: '20px',
    fontWeight: '800',
    lineHeight: '1.2',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    lineHeight: '1.3',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '1.4',
  },
};

// ==========================================
// FLAT BUTTON STYLES (CI v6.3 - LOCKED)
// ==========================================
export const EMZI_BUTTON_STYLES = {
  // Primary Button (Solid Blue) - Medium
  primary: {
    backgroundColor: EMZI_COLORS.primary,
    color: EMZI_COLORS.textWhite,
    border: 'none',
    borderRadius: EMZI_RADIUS.md,
    padding: '10px 16px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '40px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  
  // Orange CTA Button (Flat) - Medium
  cta: {
    backgroundColor: EMZI_COLORS.accent,
    color: EMZI_COLORS.textWhite,
    border: 'none',
    borderRadius: EMZI_RADIUS.md,
    padding: '10px 16px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '40px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  
  // Outlined Button (White background with border)
  outlined: {
    backgroundColor: EMZI_COLORS.background,
    color: EMZI_COLORS.primary,
    border: `1px solid ${EMZI_COLORS.border}`,
    borderRadius: EMZI_RADIUS.md,
    padding: '10px 16px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '40px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  
  // Small Button (Compact) - 36px
  small: {
    backgroundColor: EMZI_COLORS.primary,
    color: EMZI_COLORS.textWhite,
    border: 'none',
    borderRadius: EMZI_RADIUS.sm,
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '36px',
    transition: 'all 0.2s ease',
  },
  
  // Icon Button - 32px square
  icon: {
    backgroundColor: 'transparent',
    color: EMZI_COLORS.primary,
    border: `1px solid ${EMZI_COLORS.border}`,
    borderRadius: EMZI_RADIUS.sm,
    padding: '6px',
    cursor: 'pointer',
    minHeight: '32px',
    minWidth: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  
  // Danger Button
  danger: {
    backgroundColor: EMZI_COLORS.error,
    color: EMZI_COLORS.textWhite,
    border: 'none',
    borderRadius: EMZI_RADIUS.md,
    padding: '10px 16px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '40px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  
  // Success Button
  success: {
    backgroundColor: EMZI_COLORS.success,
    color: EMZI_COLORS.textWhite,
    border: 'none',
    borderRadius: EMZI_RADIUS.md,
    padding: '10px 16px',
    fontWeight: '600',
    cursor: 'pointer',
    minHeight: '40px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
};

// ==========================================
// HELPER FUNCTIONS (APPROVED PATTERNS)
// ==========================================

/**
 * Generate EMZI Blue Flat Header (v6.3)
 * Used for: Page headers, main panels
 */
export const getHeaderFlat = () => ({
  backgroundColor: EMZI_COLORS.primary,
  color: EMZI_COLORS.textWhite,
  boxShadow: EMZI_SHADOWS.header,
});

/**
 * @deprecated Use getHeaderFlat() instead
 * Legacy function for backward compatibility
 */
export const getHeaderGradient = () => getHeaderFlat();

/**
 * @deprecated Use getHeaderFlat() instead
 * Legacy function for backward compatibility
 */
export const getHeaderSolid = () => getHeaderFlat();

/**
 * Generate Card Style
 * Used for: Content cards, panels
 */
export const getCardStyle = (hover = true) => ({
  backgroundColor: EMZI_COLORS.background,
  borderRadius: EMZI_RADIUS.md,
  boxShadow: hover ? EMZI_SHADOWS.card : EMZI_SHADOWS.sm,
  border: `1px solid ${EMZI_COLORS.border}`,
  transition: 'all 0.2s ease',
});

/**
 * Generate Control Button Style (Approved Standard)
 * This is the LOCKED button style matching Sign Out button
 * Used for: System Control buttons, primary actions on blue backgrounds
 */
export const getControlButtonStyle = () => ({
  backgroundColor: 'transparent',
  color: EMZI_COLORS.textWhite,
  border: `2px solid ${EMZI_COLORS.borderWhite}`,
  borderRadius: EMZI_RADIUS.md,
  padding: '12px 8px',
  fontWeight: '700',
  cursor: 'pointer',
  boxShadow: EMZI_SHADOWS.button,
  transition: 'all 0.2s ease',
  minHeight: '64px',
});

/**
 * Generate Button Hover State
 */
export const getButtonHoverStyle = () => ({
  backgroundColor: EMZI_COLORS.hoverBg,
  transform: 'scale(1.05)',
  boxShadow: EMZI_SHADOWS.buttonHover,
});

/**
 * Get Button Style by Variant
 * @param {string} variant - 'primary' | 'outlined' | 'danger' | 'success'
 */
export const getButtonStyle = (variant = 'primary') => {
  return EMZI_BUTTON_STYLES[variant] || EMZI_BUTTON_STYLES.primary;
};

// ==========================================
// STATUS PILLS & CHIPS (FLAT CI v6.3)
// ==========================================

/**
 * Status Pill Styles
 * Used for: Campaign status badges
 */
export const STATUS_PILL_STYLES = {
  active: {
    backgroundColor: '#10B981',
    color: '#FFFFFF',
    borderRadius: EMZI_RADIUS.full,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  upcoming: {
    backgroundColor: '#F59E0B',
    color: '#FFFFFF',
    borderRadius: EMZI_RADIUS.full,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  expired: {
    backgroundColor: '#9CA3AF',
    color: '#FFFFFF',
    borderRadius: EMZI_RADIUS.full,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  archived: {
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    borderRadius: EMZI_RADIUS.full,
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
};

/**
 * Info Chip Styles
 * Used for: Date chips, team count chips, region chips
 */
export const getChipStyle = () => ({
  backgroundColor: EMZI_COLORS.surface,
  border: `1px solid ${EMZI_COLORS.border}`,
  borderRadius: EMZI_RADIUS.sm,
  padding: '4px 8px',
  fontSize: '12px',
  fontWeight: '400',
  color: EMZI_COLORS.textSecondary,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
});

/**
 * Get Status Pill Style
 * @param {string} status - 'active' | 'upcoming' | 'expired' | 'archived'
 */
export const getStatusPillStyle = (status) => {
  return STATUS_PILL_STYLES[status] || STATUS_PILL_STYLES.active;
};

